// Shared Sentry `beforeSend` for the client, server, and edge inits
// (instrumentation-client.ts, sentry.server.config.ts, sentry.edge.config.ts).
//
// Two responsibilities, in order:
//
//  1. DROP deployment version-skew errors entirely (return null). The repo
//     already classifies these as "NOT real bugs" in lib/deploy/versionSkew.ts:
//     the running client loaded JS chunks or Server Actions from a deployment
//     that has since been replaced. Vercel Skew Protection prevents almost all
//     of them, and the BuildWatcher surfaces the rare leftover as a calm "nueva
//     versión" prompt — never a crash. They must not page anyone or inflate the
//     Sentry issue stream. Skew shows up on BOTH sides (client: ChunkLoadError /
//     "Failed to fetch dynamically imported module"; server: "Failed to find
//     Server Action"), so the drop lives here, shared by every runtime.
//
//  2. NAME raw database errors. A Supabase `{ code, message, details, hint }`
//     passed straight to `captureException` arrives with whatever class name it
//     has in the minified production bundle — the money issues REDY-35 («f»),
//     REDY-36 («o») and REDY-2B («Y») were unreadable in the issue list. New
//     code should call `captureDbError` (lib/sentry/captureDbError.ts); this is
//     the safety net for the sites that still don't. Tagged
//     `db_error_unwrapped:true` so the leftovers can be found in Sentry.
//
//  3. Otherwise scrub PII (C-19) and return the event.
import type { ErrorEvent, EventHint } from "@sentry/nextjs"
import { isVersionSkewError } from "@/lib/deploy/versionSkew"
import { scrubPiiBeforeSend } from "@/lib/sentry/pii-scrubber"
import { isPostgresLikeError, redactDbText } from "@/lib/sentry/captureDbError"

export function sentryBeforeSend(
  event: ErrorEvent,
  hint: EventHint,
): ErrorEvent | null {
  if (isVersionSkewError(hint?.originalException)) return null
  nameRawDbError(event, hint?.originalException)
  return scrubPiiBeforeSend(event, hint)
}

function nameRawDbError(event: ErrorEvent, original: unknown): void {
  // Un DbError ya envuelto es un `Error` de verdad: no tiene la forma cruda.
  if (original instanceof Error || !isPostgresLikeError(original)) return
  const values = event.exception?.values
  const ex = values?.[values.length - 1]
  if (!ex) return
  const message = (redactDbText(original.message) ?? "").replace(/\s+/g, " ").trim().slice(0, 120)
  ex.type = "DbError"
  ex.value = `${original.code} ${message}`.trim()
  if (!event.fingerprint || event.fingerprint.length === 0) {
    event.fingerprint = ["db", "unwrapped", original.code]
  }
  event.tags = { ...event.tags, db_error_unwrapped: "true", db_code: original.code }
}
